/**
 * How a photo or video should fill its frame — the web mirror of the player's
 * ImageFit.from().
 *
 * ⚠️ 'cover' EXISTED NOWHERE IN THIS CODEBASE. Every renderer had the same
 * two-state shape — `objectFit: someBoolean ? 'fill' : 'contain'` — inherited
 * from when maintain_aspect_ratio was the only setting. So the new "Fill screen"
 * mode rendered PIXEL-IDENTICAL to "Whole image": crop and fit both map the
 * legacy boolean to true, and the preview only ever asked the boolean. A café
 * chose crop, saw bars, and reasonably concluded the feature was broken —
 * before any device was involved.
 *
 * That is one missing concept, not four bugs, so it lives in one function.
 * The next renderer someone writes has this to call instead of reinventing the
 * boolean.
 *
 * ⚠️ THE PRECEDENCE MUST NOT DRIFT FROM THE PLAYER. This is deliberately the
 * same order as ImageFit.from() in the Android app
 * (utlis/ImageFit.kt): the explicit mode wins, an unrecognised or absent mode
 * falls back to the legacy boolean. A preview that resolves differently from
 * the screen is worse than no preview, because it is confidently wrong.
 *
 * @param {string|null|undefined} mode 'fit' | 'crop' | 'fill'
 * @param {boolean|null|undefined} maintainAspectRatio the legacy boolean
 * @returns {'contain'|'cover'|'fill'} a CSS object-fit value
 */
/**
 * Read the saved display mode off a media row.
 *
 * ⚠️ IT IS NOT A TOP-LEVEL FIELD ON THE ROW. image_fit lives inside the pivot's
 * `settings` JSON, which getCampaignContent returns as `media_settings` (both
 * branches json_decode it, the media one unwrapping double-encoding). Reading
 * `item.image_fit` — as this dialog did — is always undefined, so a photo saved
 * as crop reopened showing "Whole image": the write worked and the read did not.
 *
 * Tolerates a string in case any path ever hands one over undecoded.
 */
export const readImageFit = (item) => {
  const raw = item?.media_settings ?? item?.settings;
  let s = raw;
  let guard = 0;
  while (typeof s === 'string' && guard++ < 5) {
    try { s = JSON.parse(s); } catch { return null; }
  }
  const fit = s && typeof s === 'object' ? s.image_fit : null;
  return ['fit', 'crop', 'fill'].includes(fit) ? fit : null;
};

/**
 * @param {string|null|undefined} mode          "fit" | "crop" | "fill"
 * @param {boolean|undefined} maintainAspectRatio ACCEPTED AND IGNORED. Kept so
 *   this stays shape-identical to the player's ImageFit.from(); see the default
 *   branch for why it stopped being consulted.
 */
// eslint-disable-next-line no-unused-vars
export const resolveObjectFit = (mode, maintainAspectRatio) => {
  switch (String(mode ?? '').trim().toLowerCase()) {
    case 'crop':
      return 'cover';
    case 'fill':
      return 'fill';
    case 'fit':
      return 'contain';
    default:
      // Absent or unknown: crop. Every existing row predates image_fit, and a
      // photo filling the screen is what a cafe wants far more often than one
      // pillarboxed in black. maintain_aspect_ratio is deliberately NOT
      // consulted here any more: it is NOT NULL DEFAULT 1, so it cannot tell
      // "nobody chose" apart from "somebody chose fit", which made it useless
      // as a signal. The cost is stated plainly: a row that deliberately set it
      // to false (stretch) now crops instead. That is accepted -- one cafe is
      // live, all of this is dev, and there is no installed base to preserve.
      // An explicit mode always wins over this fallback; only absence lands here.
      return 'cover';
  }
};

export default resolveObjectFit;
